import { useEffect } from "react";
import { Server, AlertTriangle, WifiOff, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { useErpConnectionStore } from "../../store/useErpConnectionStore";

/**
 * The ERP connection state, shown wherever work depends on it.
 *
 * Two jobs, and the second is the important one:
 *
 *   1. Say what the connection is doing.
 *   2. Say what that means for the work in front of the user — specifically,
 *      that nothing they have typed is at risk. "ERP unavailable" on its own
 *      reads as "you are about to lose this", and people respond by copying
 *      their work somewhere else or by hammering the button.
 *
 * `compact` renders a single line for a form footer; the full version is for a
 * page header where there is room to explain.
 */

type Tone = { bg: string; text: string; icon: typeof Server; label: string };

// The fallback is a named constant rather than a lookup, so an unrecognised
// state renders as "checking" instead of as nothing at all.
const CHECKING: Tone = {
  bg: "bg-gray-50 border-gray-200",
  text: "text-gray-600",
  icon: Loader2,
  label: "Checking ERP…",
};

const TONE: Record<string, Tone> = {
  Connected: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", icon: CheckCircle2, label: "ERP connected" },
  Checking: CHECKING,
  Unstable: { bg: "bg-amber-50 border-amber-300", text: "text-amber-800", icon: AlertTriangle, label: "ERP connection unstable" },
  "Connection Lost": { bg: "bg-red-50 border-red-300", text: "text-red-700", icon: WifiOff, label: "ERP connection lost" },
  "Not Configured": { bg: "bg-gray-50 border-gray-200", text: "text-gray-600", icon: Server, label: "ERP not configured" },
};

const since = (iso?: string | null) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return `${Math.round(mins / 60)}h ago`;
};

const ErpConnectionBanner = ({
  compact = false,
  /** Hidden while healthy — for forms, where a green bar is just noise. */
  onlyWhenProblem = false,
}: {
  compact?: boolean;
  onlyWhenProblem?: boolean;
}) => {
  const { connection, isChecking, check, watch, unwatch } = useErpConnectionStore();

  useEffect(() => {
    watch();
    return () => unwatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!connection) return null;
  if (onlyWhenProblem && connection.canSubmit) return null;

  const tone = TONE[connection.state] ?? CHECKING;
  const Icon = tone.icon;
  const spinning = connection.state === "Checking" || isChecking;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold ${tone.text}`}>
        <Icon size={13} className={spinning ? "animate-spin" : ""} />
        {tone.label}
      </span>
    );
  }

  return (
    <div className={`rounded-xl border p-3 ${tone.bg}`}>
      <div className="flex items-start gap-2.5">
        <Icon size={16} className={`${tone.text} mt-0.5 shrink-0 ${spinning ? "animate-spin" : ""}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[13px] font-bold ${tone.text}`}>{tone.label}</span>
            {connection.lastCheckedAt && (
              <span className="text-[11px] text-gray-500">checked {since(connection.lastCheckedAt)}</span>
            )}
          </div>

          {connection.reason && (
            <p className="text-[12px] text-gray-700 mt-0.5 leading-relaxed">{connection.reason}</p>
          )}

          {/* The part that stops people panicking. */}
          {!connection.canSubmit && connection.state !== "Not Configured" && (
            <p className="text-[12px] text-gray-700 mt-1.5 leading-relaxed">
              Anything you have already entered is saved in Omni Workspace and will not be lost.
              Submissions to ERP are being held until the connection is reliable again.
            </p>
          )}

          {connection.incident && (
            <p className="text-[11px] text-gray-600 mt-1.5">
              Ongoing since {new Date(connection.incident.since).toLocaleTimeString()}
              {connection.incident.blockedSubmissions > 0 &&
                ` · ${connection.incident.blockedSubmissions} submission${
                  connection.incident.blockedSubmissions === 1 ? "" : "s"
                } waiting`}
              {" · IT has been notified"}
            </p>
          )}
        </div>

        <button
          onClick={() => check({ force: true })}
          disabled={isChecking}
          title="Check now"
          className={`p-1.5 rounded-lg hover:bg-black/5 shrink-0 ${tone.text} disabled:opacity-50`}
        >
          <RefreshCw size={14} className={isChecking ? "animate-spin" : ""} />
        </button>
      </div>
    </div>
  );
};

export default ErpConnectionBanner;
